import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import autosuggestHighlightMatch from "autosuggest-highlight/match/index";
import autosuggestHighlightParse from "autosuggest-highlight/parse/index";
import { debounce } from "throttle-debounce";
import * as Yup from "yup";
import leftPad from "left-pad";
import { Trans, withNamespaces } from "react-i18next";
import Autosuggest from "ati-ui-react/components/Autosuggest";
import Button from "ati-ui-react/components/Button";
import Checkbox from "ati-ui-react/components/Checkbox";
import QuestionIcon from "ati-ui-react/widgets/QuestionIcon";
import RestrictedInput from "ati-ui-react/components/RestrictedInput";
import ButtonGroup from "ati-ui-react/components/ButtonGroup";
import AtiTooltip from "ati-ui-react/components/Tooltip";
import RouteNotFound from "../RouteNotFound";
import FormField from "../FormField";
import FormikDatePicker from "../FormikDatepicker";
import { useFormik } from "formik";
import ErrorPanel from "../ErrorPanel";
import { INPUT_RESTRICTION } from "../../constants";
import styles from "./FilterPanel.scss";
import { GOALS, reachGoal } from "../../utils/gtm";
import {
  createDefaultRouteRequestFromPoint,
  createDefaultRouteRequestPoint,
  createDefaultRouteRequestThroughPoint,
  createDefaultRouteRequestToPoint,
  fillRouteRequestPointFromCity,
  fillRouteRequestPointFromGeoCodedObject,
  formatGeoPoint
} from "../../bl/routeRequestUtils";
import { batch } from "react-redux";
import { headerBottomHeight } from "../../utils";
import { getAddress } from "../../utils/addressHelper";
import { parseCoordinates } from "../../utils/coordinatesString";
import geoStorages from "../../utils/SessionStorageRingBuffer";
import RouteRequest from "../../models/RouteRequest";
import api from "../../api";
import { useAsyncFieldValidator } from "../../hooks/useAsyncFieldValidator";
import { intersectObjects } from "../../bl/routeService";

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14">
    <path
      d="M8.637.988h2.601V2.5H.762V.988h2.601L4.137.25h3.726l.774.738zM3.328 6.895l1.617 1.617-1.582 1.582 1.055 1.054L6 9.566l1.582 1.582 1.055-1.054-1.582-1.582 1.582-1.617L7.582 5.84 6 7.457 4.418 5.84l-1.09 1.055zM1.5 12.238v-9h9v9c0 .399-.152.75-.457 1.055-.305.305-.656.457-1.055.457H3.012c-.399 0-.75-.152-1.055-.457-.305-.305-.457-.656-.457-1.055z" />
  </svg>
);

const Fields = {
  from: "from",
  to: "to",
  through: "through"
};

const autosuggestValidationSchema = (t, optional) => {
  let schema = Yup.object()
    .shape({
      value: Yup.string(),
      cityId: Yup.number().notRequired(),
    })
    .required(t("trace-filters-validation-required-error-message", "Нужно заполнить"))
    .test(
      "is-valid-city",
      t("trace-filters-validation-not-selected-error-message", "Выберите город из списка"),
      (value) => !value.isEmpty() || optional && (!value || value.value === "")
    );
  if (optional) {
    schema = schema.notRequired();
  }
  return schema;
};

class AutosuggestValue {
  /** @param {Object} data
   * @param {number} [data.cityId]
   * @param {number} [data.countryId]
   * @param {RouteRequestPoint} [data.geoCodedRouteRequestPoint]
   * @param {string} [data.value]
   */
  constructor({ cityId, countryId, geoCodedRouteRequestPoint, value = "" } = {}) {
    /** @type {number} */
    this.cityId = cityId;
    /** @type {number} */
    this.countryId = countryId;
    /** @type {RouteRequestPoint} */
    this.geoCodedRouteRequestPoint = geoCodedRouteRequestPoint;
    /** @type {string} */
    this.value = value;
  }

  isEmpty() {
    return !this.cityId && !this.geoCodedRouteRequestPoint;
  }
}

const FilterPanel = (props) => {
  const maxThroughCitiesCount = 18;

  const {
    routeRequest,
    routeNotFound,
    suggestions,
    isRouteSearching,
    fetchSuggestions,
    clearSuggestions,
    calculations,
    restrictions,
    updateRouteRequest,
    searchRoute,
    t
  } = props;

  const RouteNotFoundBlock = useRef(null);

  const { runPromise, isLoading } = useAsyncFieldValidator();
  const [scrollToError, setScrollToError] = useState(true);
  const isSelectingSuggestionRef = useRef(false);

  /** @param {RouteRequestPoint} routeRequestPoint */
  const getAutosuggestValue = (routeRequestPoint) => {
    if (!routeRequestPoint) {
      return "";
    }
    return routeRequestPoint.city
      ? getAddress(routeRequestPoint)
      : routeRequestPoint.geoPoint ? formatGeoPoint(routeRequestPoint.geoPoint) : "";
  };

  /**
   * Создает маршрут из точек, выбранных пользователем (и по cityId и координаты)
   * @param {Object} data
   * @param {AutosuggestValue} data.fromSuggestValue
   * @param {AutosuggestValue} data.toSuggestValue
   * @param {AutosuggestValue[]} data.throughSuggestValues
   * @returns { RouteRequest}
   */
  const createRouteRequest = async ({ fromSuggestValue, toSuggestValue, throughSuggestValues }) => {

    const allValues = [fromSuggestValue, ...throughSuggestValues, toSuggestValue];
    const cityIds = allValues.map(value => value.cityId);

    /** @type {City[]} */
    let cities = [];
    if (cityIds && cityIds.length > 0) {
      cities = await geoStorages.cities.getByIds(cityIds);
    }

    /** @type {Map<number, City>} */
    const citiesMap = new Map(cities.map(c => [c.id, c]));

    /** @type {RouteRequestPoint} */
    let from;
    if (fromSuggestValue.cityId) {
      const fromCity = citiesMap.get(fromSuggestValue.cityId);
      from = createDefaultRouteRequestFromPoint();
      fillRouteRequestPointFromCity(from, fromCity);
    } else {
      from = fromSuggestValue.geoCodedRouteRequestPoint;
    }

    /** @type {RouteRequestPoint} */
    let to;
    if (toSuggestValue.cityId) {
      const toCity = citiesMap.get(toSuggestValue.cityId);
      to = createDefaultRouteRequestToPoint();
      fillRouteRequestPointFromCity(to, toCity);
    } else {
      to = toSuggestValue.geoCodedRouteRequestPoint;
    }

    const throughPromises = throughSuggestValues.filter(item => !item.isEmpty()).map(async item => {
      /** @type {RouteRequestPoint} */
      let through;

      if (item.cityId) {
        const throughCity = citiesMap.get(item.cityId);
        through = createDefaultRouteRequestThroughPoint();
        fillRouteRequestPointFromCity(through, throughCity);
      } else {
        through = item.geoCodedRouteRequestPoint;
      }
      return through;
    });

    const through = await Promise.all(throughPromises);
    return new RouteRequest({ from, to, through });
  };

  const formik = useFormik({
    initialValues: {
      ...calculations,
      departureHour: leftPad(calculations.departureHour, 2, "0"),
      departureMinute: leftPad(calculations.departureMinute, 2, "0"),
      ...restrictions,
      from: new AutosuggestValue(),
      to: new AutosuggestValue(),
      through: []
    },
    validateOnChange: false,
    validationSchema: Yup.object().shape({
      from: autosuggestValidationSchema(t, false),
      to: autosuggestValidationSchema(t, false),
      through: Yup.array().of(autosuggestValidationSchema(t, true)),
      workingDay: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(1, t("trace-filters-validation-invalid-hours-min-value-error-message", "Не менее, чем 1 час")).max(24, t("trace-filters-validation-invalid-hours-max-value-error-message", "Не более, чем 24 часа")),
      dailyMileage: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(1, t("trace-filters-validation-value-less-than-1-error-message", "Не менее, чем 1")),
      fuelConsumption: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(1, t("trace-filters-validation-value-less-than-1-error-message", "Не менее, чем 1")),
      fuelPrice: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(1, t("trace-filters-validation-value-less-than-1-error-message", "Не менее, чем 1")),
      departureDate: Yup.mixed().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")),
      departureHour: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(0, t("trace-filters-validation-invalid-departure-hours-min-value-error-message", "Не менее, чем 0 часов")).max(23, t("trace-filters-validation-invalid-departure-hours-max-value-error-message", "Не более, чем 23 часа")),
      departureMinute: Yup.number().required(t("trace-filters-validation-required-error-message", "Нужно заполнить")).min(0,t("trace-filters-validation-invalid-departure-minutes-min-value-error-message", "Не менее, чем 0 минут")).max(59, t("trace-filters-validation-invalid-departure-minutes-max-value-error-message", "Не более, чем 59 минут")),
    }),
    onSubmit: (values) => {
      const calculations = {
        departureDate: values.departureDate.startOf("day"),
        departureHour: parseInt(values.departureHour, 10),
        departureMinute: parseInt(values.departureMinute, 10),
        fuelConsumption: parseFloat(values.fuelConsumption),
        fuelPrice: parseFloat(values.fuelPrice),
        workingDay: parseInt(values.workingDay, 10),
        dailyMileage: parseFloat(values.dailyMileage),
      };
      const restrictions = {
        allowFerries: values.allowFerries,
        allowWinterRoads: values.allowWinterRoads,
        excludeMkad: values.excludeMkad,
        excludeTollRoads: values.excludeTollRoads,
        fastWay: values.fastWay,
        withinCountry: values.withinCountry,
      };
      batch(async () => {
        const routeRequest = await createRouteRequest({
          fromSuggestValue: values.from,
          toSuggestValue: values.to,
          throughSuggestValues: values.through
        });
        updateRouteRequest(routeRequest);
        searchRoute({ calculations, restrictions });
      });
    },
  });

  const {
    values,
    errors,
    touched,
    setValues,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    getFieldProps
  } = formik;

  // debounce wrapper для fetchSuggestions
  const fetchSuggestionsWithDebounce = useCallback(
    debounce(300, (q) => fetchSuggestions(q)),
    [fetchSuggestions]
  );

  // helper проверяет, нужно ли отключить withinCountry
  const checkWithinCountryShouldBeDisabled = useCallback(() => {
    const countryIds = [values.from.countryId, ...values.through.map((item) => item.countryId), values.to.countryId].filter(Boolean);
    const uniqueCount = new Set(countryIds).size;
    return uniqueCount > 1;
  }, [values.from, values.through, values.to]);
  const isWithinCountryDisabled = checkWithinCountryShouldBeDisabled();

  useEffect(() => {
    if (routeNotFound && !isRouteSearching && scrollToError && RouteNotFoundBlock.current) {
      RouteNotFoundBlock.current.scrollIntoView();
      setScrollToError(false);
    }
  }, [routeNotFound, isRouteSearching, scrollToError]);


  useEffect(() => {
    /** @param {RouteRequestPoint} routeRequestPoint
     * @returns {AutosuggestValue} 
     */
    const createAutosuggestValueFromRouteRequestPoint = (routeRequestPoint) =>
      new AutosuggestValue({
        cityId: routeRequestPoint.isCenterCityPoint ? routeRequestPoint.city?.id : undefined,
        countryId: routeRequestPoint.country?.id,
        geoCodedRouteRequestPoint: !routeRequestPoint.isCenterCityPoint && !routeRequestPoint.isEmpty() ? routeRequestPoint : undefined,
        value: getAutosuggestValue(routeRequestPoint)
      });

    const through = routeRequest.through.map(item => createAutosuggestValueFromRouteRequestPoint(item));
    if (through.length < maxThroughCitiesCount) {
      through.push(new AutosuggestValue());
    }

    setValues({
      ...values,
      from: createAutosuggestValueFromRouteRequestPoint(routeRequest.from),
      to: createAutosuggestValueFromRouteRequestPoint(routeRequest.to),
      through: through
    });
  }, [routeRequest.from, routeRequest.to, routeRequest.through]);

  useEffect(() => {
    if (isWithinCountryDisabled) {
      setFieldValue("withinCountry", false);
    }
  }, [isWithinCountryDisabled]);

  // === Handlers ===
  const handleInputBlur = (event) => {
    const { name } = event.currentTarget;
    let { value } = event.currentTarget;

    if (Number.parseInt(value, 10) < 10) {
      value = `0${value}`.slice(-2);
    }

    setFieldValue(name, value);
    handleBlur(event);
  };

  const getFieldPath = (id) => {
    return (id === Fields.from || id === Fields.to)
      ? id
      : `${Fields.through}[${id}]`;
  };

  const getAutosuggestValueByFieldPath = (fieldPath) => {
    return getFieldProps(fieldPath).value;
  };

  const setAutosuggestValueById = (id, value, shouldValidate = false) => {
    if (id === Fields.from || id === Fields.to) {
      setFieldValue(id, value, shouldValidate);
    } else {
      // через точки
      const next = [...values.through];
      const index = parseInt(id, 10);
      next[index] = value;
      if (next.length < maxThroughCitiesCount && !next[next.length - 1].isEmpty()) {
        next.push(new AutosuggestValue());
      }
      setFieldValue(Fields.through, next, shouldValidate);
    }
  };

  const handleSuggestionValueChanged = (value, id) => {
    const fieldPath = getFieldPath(id);

    // Блокируем лишние setFieldValue, если значение текста не изменилось
    const prev = getAutosuggestValueByFieldPath(fieldPath);
    if (prev && prev.value === value) {
      return;
    }

    const newSuggestValue = new AutosuggestValue({ value });
    // Не валидируем на onChange, чтобы избежать промежуточной ошибки до onSuggestionSelected
    setAutosuggestValueById(id, newSuggestValue, false);
  };

  const handleSuggestionSelected = (event, { suggestion }, id) => {
    isSelectingSuggestionRef.current = true;
    const newSuggestValue = new AutosuggestValue({ cityId: suggestion.cityId, countryId: suggestion.countryId, value: suggestion.address });
    // Применяем значение с немедленной валидацией и снимаем подсветку ошибки
    setAutosuggestValueById(id, newSuggestValue, true);
    const fieldPath = getFieldPath(id);
    setFieldTouched(fieldPath, false, false);
    // Сбрасываем флаг после того, как Autosuggest вызовет очистку подсказок
    // Это «отложенная валидация»: setTimeout(..., 0) переносит сброс флага на следующий тик событийного цикла, чтобы:
    //  - дать React/Formik применить setFieldValue и обновить стейт;
    //  - дождаться закрытия дропдауна и вызова onSuggestionsClearRequested (который часто приходит сразу после выбора) — иначе они гоняются друг с другом и получаются двойные/«грязные» проверки со старым значением;
    //  - не ставить touched и не подсвечивать ошибку в момент выбора.
    setTimeout(() => { isSelectingSuggestionRef.current = false; }, 0);
  };

  const handleRemoveIntermediateCity = (id) => {
    const through = values.through;
    const next = [...through.slice(0, id), ...through.slice(id + 1)];
    if (next.length < maxThroughCitiesCount && !next[next.length - 1].isEmpty()) {
      next.push(new AutosuggestValue());
    }
    setFieldValue(Fields.through, next);
  };

  /** Вызывается, когда пользователь кликает в поле автокомплита */
  const handleSuggestionsFetchRequested = ({ value, reason }) => {
    value && reason !== "input-focused" ? fetchSuggestionsWithDebounce(value) : fetchSuggestions(value);
  };

  /** Вызывается, когда пользователь нажимает на Esc или когда теряется фокус */
  const handleSuggestionsClearRequested = async (id) => {
    clearSuggestions();
    const fieldPath = getFieldPath(id);
    const prev = getAutosuggestValueByFieldPath(fieldPath);

    // Во время выбора подсказки не трогаем touched/валидацию, чтобы не залипали ошибки
    if (isSelectingSuggestionRef.current) {
      return;
    }

    // Если уже есть валидный выбор — выходим без изменения touched
    const hasSelection = prev && (prev.cityId !== undefined || prev.geoCodedRouteRequestPoint !== undefined);
    if (hasSelection) {
      return;
    }

    // Если поле пустое после очистки — отмечаем touched, чтобы показать ошибку
    if (!prev.value) {
      await setFieldTouched(fieldPath, true);
      return;
    }
    const geoPoint = parseCoordinates(prev.value);
    if (geoPoint) {

      /** @type {GeoCodingResult} */
      const geoCodingResult = (await runPromise(fieldPath, async (signal) => api.geo.reverseGeocoding([geoPoint], signal)))[0];

      const routeRequestPoint = createDefaultRouteRequestPoint();
      routeRequestPoint.isStartPoint = id === Fields.from;
      routeRequestPoint.isIntermediatePoint = id !== Fields.from && id !== Fields.to;
      routeRequestPoint.isEndPoint = id === Fields.to;
      await fillRouteRequestPointFromGeoCodedObject(routeRequestPoint, geoCodingResult);
      const value = getAutosuggestValue(routeRequestPoint);
      const newSuggestValue = new AutosuggestValue({ value, geoCodedRouteRequestPoint: routeRequestPoint });
      // Устанавливаем значение с немедленной валидацией и снимаем touched
      setAutosuggestValueById(id, newSuggestValue, true);
      await setFieldTouched(fieldPath, false, false);
      return;
    }
    // Если введён невалидный текст, отмечаем touched
    await setFieldTouched(fieldPath, true);
  };

  // Обработка нажатия Enter в поле автокомплита
  const handleAutosuggestKeyDown = async (event, id) => {
    if (event.key !== "Enter") {
      return;
    }

    // Если открыт список подсказок — предотвратить submit формы и дать Autosuggest обработать выбор
    const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
    if (hasSuggestions) {
      event.preventDefault();
      return;
    }

    await handleSuggestionsClearRequested(id);
  };

  // rendering suggestion with highlighted match
  const renderSuggestion = (suggestion, { query }) => {
    let split = [];
    const address = suggestion.address;
    const closingParenthesisIndex = address.indexOf(")");
    if (closingParenthesisIndex !== -1) {
      // обработка случая, когда в clarifiedName более, чем один район, например, Бор д. (Старорусский р-н, Новосельское с.п.), Новгородская область, Россия
      split.push(address.slice(0, closingParenthesisIndex + 1));
      split.push(...address.slice(closingParenthesisIndex + 1).split(', ').filter(Boolean));
    }
    else {
      split = address.split(", ");
    }

    const matches = autosuggestHighlightMatch(split[0], query);
    const parts = autosuggestHighlightParse(split[0], matches);

    const highlighted = parts.map((part, i) => (
      <span key={i} className={part.highlight ? styles.match : ""}>
        {part.text}
      </span>
    ));

    const tail = split.slice(1).map((el, i) => (i !== split.length - 2 ? `${el}, ` : el));
    
    return (
      <div className="suggestion">
        <b>{highlighted}</b>
        {split.length > 1 && ", "}
        {tail}
      </div>
    );
  };

  const renderThroughCitiesAutosuggests = () => (
    <>
      {values.through.map((item, index) => {
        const canBeRemoved = index < values.through.length - 1 || !item.isEmpty();
        return (
          <div className={styles.through} key={index}>
            {renderAutosuggest(index.toString(), suggestions, item.value, errors?.through && errors.through[index], Boolean(touched?.through && touched.through[index]), t)}
            {canBeRemoved &&
              (<div className={styles.tooltip}>
                <AtiTooltip
                  tooltip={t("trace-filters-field-intermediate-city-remove-button-tooltip", "Удалить точку")}>
                  <button type="button" className={styles.remove}
                    onClick={() => handleRemoveIntermediateCity(index)}>
                    <TrashIcon />
                  </button>
                </AtiTooltip>
              </div>)
            }
          </div>
        );
      })}
    </>
  );

  const renderAutosuggest = (id, suggestionsList, value, fieldError, fieldTouched, t) =>
    (
      <FormField error={fieldError} touched={fieldTouched}>
        <Autosuggest
          id={id}
          suggestions={suggestionsList}
          placeholder={t("trace-filters-field-city-placeholder", "Введите город")}
          searchProperty="address"
          valueProperty="address"
          onChange={handleSuggestionValueChanged}
          value={value}
          renderSuggestion={renderSuggestion}
          onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
          onSuggestionsClearRequested={handleSuggestionsClearRequested}
          onSuggestionSelected={handleSuggestionSelected}
          noFog
          error={fieldError && fieldTouched}
          inputProps={{
            autoComplete: "new-password", onKeyDown: (e) => {
              void handleAutosuggestKeyDown(e, id);
            }
          }}
        />
      </FormField>
    );

  const touchedFieldErrors = intersectObjects(errors, touched);
  const formHasErrors = Object.keys(touchedFieldErrors).length > 0;
  const fromFieldError = errors?.from;
  const fromFieldTouched = !!touched?.from;
  const toFieldError = errors?.to;
  const toFieldTouched = !!touched?.to;
  const searchButtonBottom = 10;

  return (
    <>
      <form>
        <div className={styles.filters}>
          <div className={styles.direction}>
            <div className={styles.row}>
              <span className={classnames(styles.label, styles.primaryLabel)}>
                {t("trace-filters-field-from-city-label", "Откуда")}
              </span>
              {renderAutosuggest(Fields.from, suggestions, values.from.value, fromFieldError, fromFieldTouched, t)}
            </div>

            <div className={styles.row}>
              <span className={classnames(styles.label, styles.primaryLabel)}>
                {t("trace-filters-field-to-city-label", "Куда")}
              </span>
              {renderAutosuggest(Fields.to, suggestions, values.to.value, toFieldError, toFieldTouched, t)}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>{t("trace-filters-field-intermediate-cities-label", "Через")}</span>
              {renderThroughCitiesAutosuggests()}
            </div>
          </div>

          <div className={styles.route}>
            <div className={styles.row}>
              <span className={classnames(styles.label, styles.primaryLabel)}>
                {t("trace-filters-field-route-label", "Маршрут")}
              </span>
              <ButtonGroup
                className={styles.routeTypeButtons}
                selectedValue={values.fastWay}
                options={[
                  { value: true, label: t("trace-filters-field-route-fastest", "Быстрый") },
                  { value: false, label: t("trace-filters-field-route-shortest", "Короткий") },
                ]}
                valueProp="value"
                displayProp="label"
                onSelectedValueChanged={value => setFieldValue("fastWay", value)}
              />
            </div>

            <div className={styles.checkBoxPanel}>
              <div className={styles.row}>
                <Checkbox
                  className={styles.routeCheckbox}
                  name="withinCountry"
                  checked={values.withinCountry}
                  onChange={({ target: { checked } }) => setFieldValue("withinCountry", checked)}
                  label={t("trace-filters-filter-within-country", "в пределах страны")}
                  size="small"
                  disabled={isWithinCountryDisabled}
                />
              </div>

              <div className={styles.row}>
                <Checkbox
                  className={styles.routeCheckbox}
                  name="excludeMkad"
                  checked={values.excludeMkad}
                  onChange={({ target: { checked } }) => setFieldValue("excludeMkad", checked)}
                  label={t("trace-filters-filter-exclude-mkad", "исключить МКАД")}
                  size="small"
                />
              </div>

              <div className={styles.row}>
                <Checkbox
                  className={styles.routeCheckbox}
                  name="allowFerries"
                  checked={values.allowFerries}
                  onChange={({ target: { checked } }) => setFieldValue("allowFerries", checked)}
                  label={t("trace-filters-filter-allow-ferries", "разрешить паромы")}
                  size="small"
                />
              </div>

              <div className={styles.row}>
                <div className={styles.flex}>
                  <div>
                    <Checkbox
                      name="allowWinterRoads"
                      checked={values.allowWinterRoads}
                      onChange={({ target: { checked } }) => setFieldValue("allowWinterRoads", checked)}
                      label={t("trace-filters-filter-allow-winter-roads", "разрешить зимники")}
                      size="small"
                    />
                  </div>
                  <div className={styles.hint}>
                    <QuestionIcon
                      className={styles.questionMark}
                      tooltip={
                        // eslint-disable-next-line
                        <Trans i18nKey="trace-filters-filter-allow-winter-roads-tooltip">
                          Дороги,&nbsp;по&nbsp;которым&nbsp;можно
                          проехать&nbsp;только&nbsp;при&nbsp;минусовой
                          температуре,&nbsp;например,
                          по&nbsp;замерзшей&nbsp;реке&nbsp;или&nbsp;озеру
                        </Trans>
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.row}>
                <Checkbox
                  className={styles.routeCheckbox}
                  name="excludeTollRoads"
                  checked={values.excludeTollRoads}
                  onChange={({ target: { checked } }) => {
                    setFieldValue("excludeTollRoads", checked);
                    reachGoal(GOALS.clickFreeRoadsButton);
                  }}
                  label={t("trace-filters-filter-exclude-toll-roads", "без проезда по платным дорогам")}
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.calculation}>
          <div className={styles.inputs}>
            <div className={styles.block}>
              <div className={styles.element}>
                <span className={styles.primaryLabel}>{t("trace-filters-time-label", "Время в пути")}</span>
                <span className={styles.label}>{t("trace-filters-filter-workday-label", "Раб. день водителя")}</span>
                <FormField error={errors.workingDay} touched={touched.workingDay}>
                  <div className={styles.input}>
                    <RestrictedInput
                      name="workingDay"
                      inputRestriction={INPUT_RESTRICTION.numbersOnly}
                      maxLength={2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="text"
                      value={values.workingDay}
                      error={errors.workingDay && touched.workingDay}
                    />
                  </div>
                  <span className={styles.unit}>{t("trace-common-units-hours-abbreviation", "ч")}</span>
                </FormField>
              </div>
              <div className={styles.element}>
                <span
                  className={styles.label}>{t("trace-filters-filter-mileage-label", "Макс. пробег в день")}</span>
                <FormField error={errors.dailyMileage} touched={touched.dailyMileage}>
                  <div className={styles.input}>
                    <RestrictedInput
                      name="dailyMileage"
                      inputRestriction={INPUT_RESTRICTION.zeroTo9999Float}
                      maxLength={5}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="text"
                      value={values.dailyMileage}
                      error={errors.dailyMileage && touched.dailyMileage}
                    />
                  </div>
                  <span
                    className={styles.unit}>{t("trace-common-units-kilometers-abbreviation", "км")}</span>
                </FormField>
              </div>

              <div className={styles.element}>
                <span className={styles.label}>{t("trace-filters-filter-departure-label", "Отправление")}</span>
                <div className={styles.departure}>
                  <FormField error={errors.departureDate} touched={!!touched.departureDate}>
                    <FormikDatePicker
                      name="departureDate"
                      date={values.departureDate}
                      error={errors.departureDate && !!touched.departureDate}
                      onChange={(name, val) => setFieldValue(name, val)}
                      onBlur={(name) => setFieldTouched(name, true)}
                    />
                  </FormField>
                  <span className={styles.at}>{t("trace-filters-filter-departure-at", "в")}</span>
                  <FormField
                    error={errors.departureHour || errors.departureMinute}
                    touched={touched.departureHour || touched.departureMinute}
                  >
                    <div className={styles.time}>
                      <div className={styles.input}>
                        <RestrictedInput
                          name="departureHour"
                          inputRestriction={INPUT_RESTRICTION.zeroTo99}
                          maxLength={2}
                          onChange={handleChange}
                          onBlur={handleInputBlur}
                          type="text"
                          value={values.departureHour}
                          error={errors.departureHour || errors.departureMinute}
                        />
                      </div>
                      <span className={styles.separator}>:</span>
                      <div className={styles.input}>
                        <RestrictedInput
                          name="departureMinute"
                          inputRestriction={INPUT_RESTRICTION.numbersOnly}
                          maxLength={2}
                          onChange={handleChange}
                          onBlur={handleInputBlur}
                          type="text"
                          value={values.departureMinute.toString()}
                          error={errors.departureHour || errors.departureMinute}
                          clearZeroOnBlur={false}
                        />
                      </div>
                    </div>
                  </FormField>
                </div>
              </div>
            </div>

            <div className={styles.block}>
              <div className={styles.element}>
                <span
                  className={styles.primaryLabel}>{t("trace-filters-fuel-consumption-label", "Расход топлива")}</span>
                <span className={styles.label}>{t("trace-filters-filter-fuel-consumption-label", "На 100 км")}</span>
                <FormField error={errors.fuelConsumption} touched={touched.fuelConsumption}>
                  <div className={styles.input}>
                    <RestrictedInput
                      name="fuelConsumption"
                      inputRestriction={INPUT_RESTRICTION.numbersOnly}
                      maxLength={3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="text"
                      value={values.fuelConsumption}
                      error={errors.fuelConsumption && touched.fuelConsumption}
                    />
                  </div>
                  <span className={styles.unit}>{t("trace-common-units-liters-abbreviation", "л")}</span>
                </FormField>
              </div>

              <div className={styles.element}>
                <span className={styles.label}>{t("trace-filters-filter-fuel-price-label", "За литр топлива")}</span>
                <FormField error={errors.fuelPrice} touched={touched.fuelPrice}>
                  <div className={styles.input}>
                    <RestrictedInput
                      name="fuelPrice"
                      inputRestriction={INPUT_RESTRICTION.zeroTo9999Float}
                      maxLength={5}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="text"
                      value={values.fuelPrice}
                      error={errors.fuelPrice && touched.fuelPrice}
                    />
                  </div>
                  <span className={styles.unit}>{t("trace-common-units-price-abbreviation", "₽")}</span>
                </FormField>
              </div>
            </div>
          </div>
        </div>

        {routeNotFound && (
          <RouteNotFound
            containerRef={(el) => {
              RouteNotFoundBlock.current = el;
            }}
          />
        )}

        {formHasErrors &&
          <ErrorPanel message={t("trace-filters-validation-general-error-message", "Заполните все поля")} />}

        <div className={styles.search} style={{ bottom: headerBottomHeight() + searchButtonBottom }}>
          <Button
            buttonType="submit"
            text={t("trace-filters-search-button-text", "Рассчитать маршрут")}
            className={styles.button}
            action={handleSubmit}
            disabled={isLoading}
            isLoading={isRouteSearching || isLoading}
          />
        </div>
      </form>
    </>
  );
};

FilterPanel.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      address: PropTypes.string.isRequired
    })
  ).isRequired,
  calculations: PropTypes.any.isRequired,
  isRouteSearching: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  fetchSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  restrictions: PropTypes.object.isRequired,
  updateRouteRequest: PropTypes.func.isRequired,
  searchRoute: PropTypes.func.isRequired,
};
export default withNamespaces()(FilterPanel);
